// Draft-text template engine — simplified per the v1 design decision: three tones,
// each with a single editable template per language. Tone keys are stable internal
// IDs; their display labels (the "Casual" / "Pro" / "Urgent" copy) are also editable
// per barber so a shop can rename them to whatever fits their voice.
//
// Why templates instead of LLM generation:
//   - Instant (no API latency between cuts)
//   - Free (no per-message cost)
//   - Reliable (no upstream API to fail, no off-brand generations)
//   - Localizable (translate once, every barber benefits)
//
// The barber edits the draft before sending anyway (it lands in their native SMS
// app pre-filled), so the LLM's "perfect" first draft is wasted effort.
//
// Variables supported (substituted via fillTemplate):
//   {firstName}            client's first name
//   {daysSinceLastVisit}   number of days since their last cut
//   {typicalGap}           their natural cycle in days
//   {lastService}          their most recent service name
//   {preferredService}     their most-used service name
//   {suggestedSlot}        a primary suggested time slot (deterministic per-client)
//   {altSlot}              an alternate slot
//   {signature}            barber's first name (or "[your name]" if not set)

export type TemplateTone = 'casual' | 'pro' | 'urgent';
export type TemplateLanguage = 'en' | 'es';

export const TEMPLATE_VARIABLES = [
  'firstName',
  'daysSinceLastVisit',
  'typicalGap',
  'lastService',
  'preferredService',
  'suggestedSlot',
  'altSlot',
  'signature',
] as const;

export type TemplateVariable = (typeof TEMPLATE_VARIABLES)[number];

export const TEMPLATE_TONES: TemplateTone[] = ['casual', 'pro', 'urgent'];
export const TEMPLATE_LANGUAGES: TemplateLanguage[] = ['en', 'es'];

// Default tone labels — barber-overridable via profile.preferences.toneLabels.
// These are starting names ("Casual", "Pro", "Urgent") that the barber can rename
// to anything that fits their voice ("Friendly check-in", "Standard", "Last call").
//
// Per-language defaults so the picker reads in the active app language when the
// barber hasn't customized a tone yet. Custom (persisted) labels are stored as a
// single string per tone — they win in any language.
export const DEFAULT_TONE_LABELS_BY_LANG: Record<TemplateLanguage, Record<TemplateTone, string>> = {
  en: { casual: 'Casual', pro: 'Pro', urgent: 'Urgent' },
  es: { casual: 'Casual', pro: 'Pro', urgent: 'Urgente' },
};

// Back-compat alias — the English defaults. Prefer DEFAULT_TONE_LABELS_BY_LANG
// + defaultToneLabel() in new code.
export const DEFAULT_TONE_LABELS: Record<TemplateTone, string> =
  DEFAULT_TONE_LABELS_BY_LANG.en;

export function defaultToneLabel(tone: TemplateTone, language: TemplateLanguage): string {
  return DEFAULT_TONE_LABELS_BY_LANG[language]?.[tone] ?? DEFAULT_TONE_LABELS_BY_LANG.en[tone];
}

// One default template per (tone, language). Picked as the strongest of the demo's
// original 4-variant pool: each one uses the most signal (days-since + suggested
// slot) and reads naturally. The barber can edit any of them in Settings.
export const SYSTEM_TEMPLATES: Record<TemplateTone, Record<TemplateLanguage, string>> = {
  casual: {
    en: "Hey {firstName} — coming up on {daysSinceLastVisit} days since your last cut. Got {suggestedSlot} open. Want it? — {signature}",
    es: "Hola {firstName} — ya van {daysSinceLastVisit} días desde tu último corte. Tengo {suggestedSlot} disponible. ¿Lo quieres? — {signature}",
  },
  pro: {
    en: "Hi {firstName} — it's been about {daysSinceLastVisit} days since your last cut. I have {suggestedSlot} available — happy to hold it for you. Let me know. — {signature}",
    es: "Hola {firstName} — han pasado como {daysSinceLastVisit} días desde tu último corte. Tengo {suggestedSlot} disponible — con gusto te lo guardo. Avísame. — {signature}",
  },
  urgent: {
    en: "{firstName} — last call on {suggestedSlot}, today only. — {signature}",
    es: "{firstName} — última llamada para {suggestedSlot}, solo hoy. — {signature}",
  },
};

const SUGGESTED_SLOTS_EN = ['Wed 3pm', 'Thu 5pm', 'Fri 4pm', 'Sat 10am', 'Sat 2pm', 'Mon 11am', 'Tue 6pm'];
const SUGGESTED_SLOTS_ES = ['Mié 3pm', 'Jue 5pm', 'Vie 4pm', 'Sáb 10am', 'Sáb 2pm', 'Lun 11am', 'Mar 6pm'];

// Hash a client id into a stable integer — same client always picks the same
// suggested slot, so reloading the app doesn't shuffle the draft.
function hashClientId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h);
}

// Fills a template string with variable values. Unknown variables stay as-is so
// unmatched tokens are visually obvious in the draft (caller can spot + remove).
export function fillTemplate(
  template: string,
  vars: Partial<Record<TemplateVariable, string | number>>
): string {
  return (template || '').replace(/\{(\w+)\}/g, (match, key) => {
    const v = vars[key as TemplateVariable];
    return v !== undefined && v !== null ? String(v) : match;
  });
}

// Backwards-compatible alias — older callers used `substitute`.
export const substitute = fillTemplate;

/* ============================================================================
 * Pick + generate
 * ============================================================================ */

export type CustomTemplateLookup = (
  tone: TemplateTone,
  language: TemplateLanguage
) => string | null;

// Pick a template body for this tone + language. If the barber has customized
// it (Settings → Text templates), use their version; else fall back to the
// system default.
export function pickTemplate(args: {
  tone: TemplateTone;
  language: TemplateLanguage;
  customLookup?: CustomTemplateLookup;
}): string {
  const { tone, language, customLookup } = args;
  const custom = customLookup?.(tone, language);
  return custom ?? SYSTEM_TEMPLATES[tone][language];
}

export type DraftVars = {
  firstName: string;
  daysSinceLastVisit: number | null;
  typicalGap: number | null;
  lastService: string | null;
  preferredService: string | null;
  suggestedSlot: string;
  altSlot: string;
  signature: string;
};

// Build the variable bag for a client. Suggested slot is hashed by client id so the
// same client gets the same primary slot across sessions.
export function buildDraftVars(args: {
  clientId: string;
  firstName: string;
  daysSinceLastVisit?: number | null;
  typicalGap?: number | null;
  preferredService?: string | null;
  signature: string;
  language: TemplateLanguage;
}): DraftVars {
  const slots = args.language === 'es' ? SUGGESTED_SLOTS_ES : SUGGESTED_SLOTS_EN;
  const slotIdx = hashClientId(args.clientId) % slots.length;
  const altIdx = (slotIdx + 3) % slots.length;
  return {
    firstName: args.firstName || 'there',
    daysSinceLastVisit: args.daysSinceLastVisit ?? null,
    typicalGap: args.typicalGap ?? null,
    lastService: args.preferredService ?? null,
    preferredService: args.preferredService ?? null,
    suggestedSlot: slots[slotIdx],
    altSlot: slots[altIdx],
    signature: args.signature,
  };
}

// One-shot helper — picks the right template + fills it in. Returns the rendered
// draft text the barber can paste into their SMS app (or send via the sms: URL).
export function generateDraftText(args: {
  tone: TemplateTone;
  language: TemplateLanguage;
  vars: DraftVars;
  customLookup?: CustomTemplateLookup;
}): string {
  const body = pickTemplate({
    tone: args.tone,
    language: args.language,
    customLookup: args.customLookup,
  });
  // Convert nullables to "—" before substitution so the rendered draft never
  // contains "null" literals where a value was missing.
  const v: Partial<Record<TemplateVariable, string>> = {
    firstName: args.vars.firstName,
    daysSinceLastVisit:
      args.vars.daysSinceLastVisit != null ? String(args.vars.daysSinceLastVisit) : '—',
    typicalGap: args.vars.typicalGap != null ? String(args.vars.typicalGap) : '—',
    lastService: args.vars.lastService ?? '',
    preferredService: args.vars.preferredService ?? '',
    suggestedSlot: args.vars.suggestedSlot,
    altSlot: args.vars.altSlot,
    signature: args.vars.signature,
  };
  return fillTemplate(body, v);
}

// Quick lookup of the system default — used by the Settings editor for the
// "Reset to default" action and for rendering the un-customized preview.
export function systemTemplate(tone: TemplateTone, language: TemplateLanguage): string {
  return SYSTEM_TEMPLATES[tone][language] ?? '';
}
